// The fake api's venue operations, past the console gate in `fake-server.ts`: the till (spec/13 §10,
// F-OPS-01 … F-OPS-06), the pass catalogue (spec/06 §3, F-PRC-01/-02/-06) and the venue library
// (spec/13 §9.1, F-CAT-02) — the reads the Shifts, Passes and Library screens make, with fixtures
// varied enough that every row, badge and sheet has something real to draw:
//
//   Shifts   an open shift at the front desk with its float and three cash movements (a pay-in, a
//            pay-out and a drop), six closed shifts (over, short, even, and a hand-over pair), and
//            today's day-close review — one shift still open, so "Close day" is blocked.
//   Passes   six products: hourly, a 3-hour pack, a 10pm–6am night pack, a weekend 5-hour pass, a
//            members-only evening pass, and one off sale.
//   Library  eight titles across launchers (Steam, Riot, Epic, Battle.net, Ubisoft, the Microsoft
//            Store, and one with no recipe yet), and a catalogue browse with four more to add.
//
// Money is integer minor units plus an ISO code, and the drawer's figures are derived the way the
// server derives them (spec/06 §2.4 invariant 6): expected cash = float + cash sales + pay-ins −
// pay-outs − drops, and a close records `counted − expected` rather than editing a balance. The
// writes are the simple ones — open, move cash, close (with the note rule and the hand-over), close
// the day, make/sell/grant a pass, add/edit/remove a title — each body checked with the contract's
// own schema. Permissions are not checked here: the screens decide what to show from `useAccess`.

import type {
  Counter,
  CounterBoard,
  RunningDrawer,
  CustomApp,
  BusinessDayClose,
  CashMovement,
  DayCloseReview,
  Game,
  LaunchRecipe,
  LibraryEntry,
  Money,
  PassHolding,
  PassProduct,
  PassProductVersion,
  Problem,
  Shift,
  ShiftCashEvent,
  ShiftTenderCount,
  VenueCatalogueGame,
} from '@clavian/api-client';
import {
  AddLibraryEntrySchema,
  CreateAppIconUploadSchema,
  CreateCustomAppSchema,
  normaliseTitleDetection,
  UpdateCustomAppSchema,
  BrowseVenueCatalogueQuerySchema,
  CloseBusinessDaySchema,
  CloseShiftSchema,
  SignOffBusinessDaySchema,
  CreatePassProductRequestSchema,
  GrantPassRequestSchema,
  OpenShiftSchema,
  PageQuerySchema,
  PassHoldingQuerySchema,
  RecordCashMovementSchema,
  SellPassRequestSchema,
  ShiftCashTimelineQuerySchema,
  UpdateLibraryEntrySchema,
  UpdatePassProductRequestSchema,
  type z,
} from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

// The same organisation and venue the rest of the fake serves (fake-server.ts), re-declared here.
const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
/** The station class the fake's `GET …/station-classes` answers with ("Standard PC"). */
const STANDARD_PC = 'scl_01k5znw4hmfay8vvr14d2pf3aa';
/** The organisation's staff (fake-server.ts `STAFF_MEMBERS`): the owner and the cashier. */
const OWNER = 'mem_01k5znw4hmfay8vvr14d2pf2e1';
const CASHIER = 'mem_01k5znw4hmfay8vvr14d2pf2e2';
/** The signed-in member (fake-server.ts `GET /v1/me/access`): a shift they start or take over is theirs. */
const ME = 'mem_01k5znw4hmfay8vvr14d2pf2db';
/** A second cashier, on Counter 2's drawer when the fake seeds two (ADR-0029). */
const AMAN = 'mem_01k5znw4hmfay8vvr14d2pf2e3';
/** The player the fake's player search finds (fake-server.ts `ORG_PLAYER`). */
const PLAYER = 'ply_01k5znw4hmfay8vvr14d2pf2a1';

/** The staff's names as the server reads them (a member who has left answers null). */
function staffName(memberId: string | null): string | null {
  return memberId === OWNER || memberId === ME ? 'Riya Sharma' : memberId === AMAN ? 'Aman Verma' : memberId === null ? null : 'Kabir Mehta';
}

/**
 * The change a drawer handed back on its cash sales (ADR-0049): about one rupee in eight of the cash taken, in whole
 * rupees, as a café's counter sees it. Each fake shift's three cash sales split it 40/35/25 like their amounts.
 */
function changeOf(cashSales: number): number {
  return Math.floor(cashSales / 8 / 100) * 100;
}

/** "Now" for the fixtures: 19:30 on Saturday 26 September 2026 in Asia/Kolkata. */
const NOW = '2026-09-26T14:00:00.000Z';
/** The venue's current business day (Asia/Kolkata). */
const BUSINESS_DAY = '2026-09-26';
/** The business day before it — the one an automatic venue's rollover has closed (ADR-0029). */
const PREVIOUS_DAY = '2026-09-25';
const CURRENCY = 'INR';

export interface VenueOpsHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  /** The console gate: a refusal (401, or 403 second factor) or `undefined` to go on. */
  readonly gate: () => FakeResponse | undefined;
  /** A venue setting's stored value (`shifts.dayCloseMode`, `shifts.enabled`; ADR-0029), or undefined for its default. */
  readonly setting?: (key: string) => unknown;
  /**
   * `two`: a second drawer is open at Counter 2 (ADR-0029: several counters at once). `mine`: the front desk's drawer is
   * the signed-in member's, so their Cash tender is open (ADR-0031). One, a colleague's, by default.
   */
  readonly drawers?: 'one' | 'two' | 'mine';
  /** Whether the signed-in member holds a permission — the cash timeline's own/all rule (F-OPS-04). */
  readonly can?: (permission: string) => boolean;
  /**
   * Discord (Install for players on) and WhatsApp (off) in the library, with what the PCs said about installing them for
   * players (F-CAT-09): Discord installed on PC-01 and refused on PC-02 (signed by someone else), WhatsApp installed for
   * another Windows account only on PC-01. Off by default, so the library's other tests keep their eight titles.
   */
  readonly playersApps?: boolean;
}

// -------------------------------------------------------------------------------------------------
// Ids and money
// -------------------------------------------------------------------------------------------------

/** Crockford base32 without i, l, o and u — the TypeID alphabet (packages/contracts/src/ids). */
const ALPHABET = '0123456789abcdefghjkmnpqrstvwxyz';

/**
 * A well-formed TypeID for the fake: a fixed 23-character stem, a one-character series per kind of
 * row, and `n` in two base32 characters (0–1023). Fixtures use `n` < 32; minted rows start at 32.
 */
function mint(prefix: string, series: string, n: number): string {
  return `${prefix}_01k5znw4hmfay8vvr14d2pf${series}${ALPHABET[(n >> 5) & 31]}${ALPHABET[n & 31]}`;
}

const inr = (amount: number): Money => ({ amount, currency: CURRENCY });

// -------------------------------------------------------------------------------------------------
// Shifts (spec/13 §10)
// -------------------------------------------------------------------------------------------------

function movement(n: number, kind: CashMovement['kind'], amount: number, reason: string, createdAt: string, by: string = CASHIER): CashMovement {
  return { id: mint('cmv', '5', n), kind, amount: inr(amount), reason, recordedByMemberId: by, createdAt };
}

/** The drawer the ledger expects: float + cash sales + pay-ins − pay-outs − drops (spec/13 §10). */
function expectedCashOf(openingFloat: number, cashSales: number, movements: readonly CashMovement[]): number {
  return movements.reduce((sum, m) => sum + (m.kind === 'pay_in' ? m.amount.amount : -m.amount.amount), openingFloat + cashSales);
}

interface ShiftSpec {
  readonly n: number;
  readonly counterName: string | null;
  readonly businessDay: string;
  readonly openedBy: string;
  readonly openedAt: string;
  readonly openingFloat: number;
  readonly cashSales: number;
  readonly card: number;
  readonly upi: number;
  readonly movements?: readonly CashMovement[];
  /** For a closed shift: the close. */
  readonly closed?: {
    readonly at: string;
    readonly by: string;
    /** `counted − expected`, in paise. */
    readonly cashVariance: number;
    /** What the card reader and the UPI app reported, where the cashier entered them. */
    readonly cardCounted?: number;
    readonly upiCounted?: number;
    readonly note?: string;
    readonly carriedFrom?: number;
    readonly carriedTo?: number;
  };
}

function tenderCount(tender: ShiftTenderCount['tender'], expected: number, counted: number | null): ShiftTenderCount {
  return { tender, expected: inr(expected), counted: counted === null ? null : inr(counted), variance: counted === null ? null : inr(counted - expected) };
}

function shift(spec: ShiftSpec): Shift {
  const movements = [...(spec.movements ?? [])];
  const expected = expectedCashOf(spec.openingFloat, spec.cashSales, movements);
  const close = spec.closed;
  const counted = close === undefined ? null : expected + close.cashVariance;
  return {
    id: mint('shf', '4', spec.n),
    venueId: VENUE_ID,
    status: close === undefined ? 'open' : 'closed',
    counterName: spec.counterName,
    businessDay: spec.businessDay,
    openedByMemberId: spec.openedBy,
    openedAt: spec.openedAt,
    openingFloat: inr(spec.openingFloat),
    closedByMemberId: close?.by ?? null,
    closedAt: close?.at ?? null,
    closingNote: close?.note ?? null,
    carriedFromShiftId: close?.carriedFrom === undefined ? null : mint('shf', '4', close.carriedFrom),
    carriedToShiftId: close?.carriedTo === undefined ? null : mint('shf', '4', close.carriedTo),
    countBusinessDay: close === undefined ? null : spec.businessDay,
    members: [{ memberId: spec.openedBy, joinedAt: spec.openedAt, leftAt: close?.at ?? null }],
    movements,
    reportKind: close === undefined ? 'x' : 'z',
    expectedCash: inr(expected),
    countedCash: counted === null ? null : inr(counted),
    cashVariance: close === undefined ? null : inr(close.cashVariance),
    cashSales: inr(spec.cashSales),
    cashReceived: inr(spec.cashSales + changeOf(spec.cashSales)),
    changeGiven: inr(changeOf(spec.cashSales)),
    salesCount: 3 + (spec.card > 0 ? 1 : 0) + (spec.upi > 0 ? 1 : 0),
    openedByName: staffName(spec.openedBy),
    closedByName: staffName(close?.by ?? null),
    // Blind close: while the shift is open nothing is counted, so no tender has a variance yet.
    tenders: [
      tenderCount('cash', expected, counted),
      tenderCount('counter_card', spec.card, close?.cardCounted ?? null),
      tenderCount('counter_upi', spec.upi, close?.upiCounted ?? null),
    ],
    // F-OPS-07: over the ₹500 default alert threshold, an overage the same as a shortage.
    varianceAlerted: close !== undefined && Math.abs(close.cashVariance) > 50_000,
    createdAt: spec.openedAt,
  };
}

/** The open shift at the front desk: ₹5,000 float, a pay-in, a pay-out and a drop to the safe. */
function seedOpenShift(holder: string = CASHIER): Shift {
  return shift({
    n: 0,
    counterName: 'Front desk',
    businessDay: BUSINESS_DAY,
    openedBy: holder,
    openedAt: '2026-09-26T04:30:00.000Z',
    openingFloat: 500_000,
    cashSales: 1_284_000,
    card: 642_000,
    upi: 1_876_500,
    movements: [
      movement(0, 'pay_in', 100_000, 'Change from the bank', '2026-09-26T06:15:00.000Z'),
      movement(1, 'pay_out', 45_000, 'Milk and snacks for the café', '2026-09-26T08:40:00.000Z'),
      movement(2, 'drop', 300_000, 'Mid-shift drop to the safe', '2026-09-26T11:05:00.000Z', OWNER),
    ],
  });
}

/** Six closed shifts, newest first: short over the alert threshold, over, even (a hand-over pair), short with a note, over, even. */
function seedClosedShifts(): Shift[] {
  return [
    shift({
      n: 1,
      counterName: 'Café counter',
      businessDay: BUSINESS_DAY,
      openedBy: OWNER,
      openedAt: '2026-09-26T03:30:00.000Z',
      openingFloat: 200_000,
      cashSales: 215_000,
      card: 88_000,
      upi: 312_500,
      // ₹750 short: over the ₹500 alert threshold, so it is a "Shift variance" in "What needs you" (F-OPS-07).
      closed: {
        at: '2026-09-26T08:30:00.000Z',
        by: OWNER,
        cashVariance: -75_000,
        cardCounted: 88_000,
        upiCounted: 312_500,
        note: 'Short ₹750 — a ₹500 note went to the café float by mistake.',
      },
    }),
    shift({
      n: 2,
      counterName: 'Front desk',
      businessDay: '2026-09-25',
      openedBy: CASHIER,
      openedAt: '2026-09-25T11:30:00.000Z',
      openingFloat: 812_000,
      cashSales: 1_642_000,
      card: 905_000,
      upi: 2_210_000,
      movements: [movement(3, 'drop', 1_000_000, 'Evening drop to the safe', '2026-09-25T16:10:00.000Z')],
      closed: { at: '2026-09-25T19:45:00.000Z', by: CASHIER, cashVariance: 2_000, cardCounted: 905_000, upiCounted: 2_210_000, carriedFrom: 3 },
    }),
    shift({
      n: 3,
      counterName: 'Front desk',
      businessDay: '2026-09-25',
      openedBy: OWNER,
      openedAt: '2026-09-25T04:30:00.000Z',
      openingFloat: 500_000,
      cashSales: 612_000,
      card: 214_000,
      upi: 986_000,
      movements: [movement(4, 'pay_out', 300_000, 'Cleaning supplies', '2026-09-25T07:20:00.000Z', OWNER)],
      closed: { at: '2026-09-25T11:30:00.000Z', by: OWNER, cashVariance: 0, cardCounted: 214_000, upiCounted: 986_000, carriedTo: 2 },
    }),
    shift({
      n: 4,
      counterName: 'Front desk',
      businessDay: '2026-09-24',
      openedBy: CASHIER,
      openedAt: '2026-09-24T04:30:00.000Z',
      openingFloat: 500_000,
      cashSales: 1_120_500,
      card: 480_000,
      upi: 1_540_000,
      movements: [movement(5, 'drop', 800_000, 'Drop to the safe', '2026-09-24T12:00:00.000Z')],
      closed: {
        at: '2026-09-24T19:30:00.000Z',
        by: CASHIER,
        cashVariance: -12_000,
        cardCounted: 480_000,
        note: 'Short ₹120 — change given twice for one ₹500 note at the rush. Flagged to the owner.',
      },
    }),
    shift({
      n: 5,
      counterName: 'Front desk',
      businessDay: '2026-09-23',
      openedBy: CASHIER,
      openedAt: '2026-09-23T04:30:00.000Z',
      openingFloat: 500_000,
      cashSales: 934_000,
      card: 356_000,
      upi: 1_198_000,
      closed: { at: '2026-09-23T19:30:00.000Z', by: CASHIER, cashVariance: 500, cardCounted: 356_000, upiCounted: 1_198_000 },
    }),
    shift({
      n: 6,
      counterName: null,
      businessDay: '2026-09-22',
      openedBy: OWNER,
      openedAt: '2026-09-22T04:30:00.000Z',
      openingFloat: 400_000,
      cashSales: 702_000,
      card: 190_000,
      upi: 845_000,
      movements: [movement(6, 'pay_in', 50_000, 'Petty cash returned', '2026-09-22T09:00:00.000Z', OWNER)],
      closed: { at: '2026-09-22T18:00:00.000Z', by: OWNER, cashVariance: 0 },
    }),
  ];
}

// -------------------------------------------------------------------------------------------------
// Passes (spec/06 §3.1)
// -------------------------------------------------------------------------------------------------

const EVERY_DAY = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

type Terms = Omit<PassProductVersion, 'id' | 'productId' | 'version' | 'createdAt'>;

function product(n: number, terms: Terms, extra: { readonly active?: boolean; readonly soldToday: number; readonly sortOrder: number }): PassProduct {
  const createdAt = '2026-08-01T06:30:00.000Z';
  const id = mint('ppr', '6', n);
  return {
    id,
    version: { ...terms, id: mint('ppv', '7', n), productId: id, version: 1, createdAt },
    active: extra.active ?? true,
    sortOrder: extra.sortOrder,
    accentToken: null,
    soldToday: extra.soldToday,
    createdAt,
    updatedAt: createdAt,
  };
}

function seedPassProducts(): PassProduct[] {
  const base = { stationClassIds: [STANDARD_PC], scope: { type: 'organization' as const }, channels: ['counter' as const, 'kiosk' as const] };
  return [
    product(
      0,
      { ...base, name: 'Hourly', shortName: 'HOURLY', description: 'One hour on any PC, any time.', price: inr(6_000), durationSeconds: 3_600, validity: { type: 'days_after_purchase', days: 30 }, windows: [], label: 'standard' },
      { soldToday: 14, sortOrder: 0 },
    ),
    product(
      1,
      { ...base, name: '3-hour pack', shortName: '3 HOURS', description: 'Three hours, played in one go or across visits.', price: inr(15_000), durationSeconds: 10_800, validity: { type: 'days_after_first_use', days: 60 }, windows: [], label: 'standard' },
      { soldToday: 6, sortOrder: 10 },
    ),
    product(
      2,
      {
        ...base,
        name: 'Night pack',
        shortName: 'NIGHT',
        description: 'Eight hours overnight, 10pm to 6am.',
        price: inr(35_000),
        durationSeconds: 28_800,
        validity: { type: 'days_after_purchase', days: 7 },
        scope: { type: 'venues', venueIds: [VENUE_ID] },
        windows: [{ days: [...EVERY_DAY], start: '22:00', end: '06:00' }],
        label: 'night',
      },
      { soldToday: 3, sortOrder: 20 },
    ),
    product(
      3,
      {
        ...base,
        name: 'Weekend 5h',
        shortName: 'WKND 5H',
        price: inr(25_000),
        durationSeconds: 18_000,
        validity: { type: 'days_after_purchase', days: 14 },
        windows: [{ days: ['sat', 'sun', 'holiday'], start: '10:00', end: '23:00' }],
        label: 'weekend',
        purchaseLimits: { perPlayerPerDay: 2 },
      },
      { soldToday: 4, sortOrder: 30 },
    ),
    product(
      4,
      {
        ...base,
        name: 'Members’ evening',
        shortName: 'MEMBER EVE',
        description: 'Two weekday evening hours for members.',
        price: inr(8_000),
        durationSeconds: 7_200,
        validity: { type: 'days_after_first_use', days: 30 },
        windows: [{ days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '17:00', end: '23:00' }],
        label: 'special',
        membersOnly: ['plan_gold'],
      },
      { soldToday: 2, sortOrder: 40 },
    ),
    product(
      5,
      {
        ...base,
        name: 'Launch week double',
        shortName: 'LAUNCH 2X',
        price: inr(9_900),
        durationSeconds: 7_200,
        validity: { type: 'fixed_end', endsAt: '2026-10-31T18:29:59.000Z' },
        windows: [],
        label: 'limited',
        saleWindow: { availableFrom: '2026-09-01T00:00:00.000Z', availableUntil: '2026-09-15T18:29:59.000Z' },
      },
      { active: false, soldToday: 0, sortOrder: 50 },
    ),
  ];
}

function holding(n: number, pass: PassProduct, playerId: string, price: Money, purchasedAt: string, remainingSeconds: number): PassHolding {
  const validity = pass.version.validity;
  const expiresAt =
    validity.type === 'fixed_end'
      ? (validity.endsAt ?? null)
      : validity.type === 'days_after_purchase'
        ? new Date(Date.parse(purchasedAt) + (validity.days ?? 0) * 86_400_000).toISOString()
        : null;
  return {
    id: mint('pah', 'h', n),
    playerId,
    productId: pass.id,
    productVersionId: pass.version.id,
    name: pass.version.name,
    grantedSeconds: pass.version.durationSeconds,
    remainingSeconds,
    price,
    status: remainingSeconds === 0 ? 'exhausted' : 'active',
    purchasedAt,
    firstUsedAt: remainingSeconds === pass.version.durationSeconds ? null : purchasedAt,
    expiresAt,
    scope: pass.version.scope,
  };
}

// -------------------------------------------------------------------------------------------------
// The library and the catalogue (spec/13 §9.1, spec/14 §6)
// -------------------------------------------------------------------------------------------------

const steam = (appId: number): LaunchRecipe => ({ launcher: 'steam', type: 'uri', label: 'Steam', uri: `steam://rungameid/${appId}` });

interface CatalogueSeed {
  readonly n: number;
  readonly igdbId: number;
  readonly name: string;
  readonly summary: string;
  readonly coverImageId: string;
  readonly genres: readonly string[];
  readonly franchise: string | null;
  readonly year: number;
  readonly recipes: readonly LaunchRecipe[];
}

/** The platform catalogue, newest mirrored first; the first eight are in this venue's library. */
const CATALOGUE: readonly CatalogueSeed[] = [
  { n: 0, igdbId: 242408, name: 'Counter-Strike 2', summary: 'The next era of competitive Counter-Strike.', coverImageId: 'co7989', genres: ['Shooter', 'Tactical'], franchise: 'Counter-Strike', year: 2023, recipes: [steam(730)] },
  {
    n: 1,
    igdbId: 126459,
    name: 'Valorant',
    summary: 'A 5v5 character-based tactical shooter.',
    coverImageId: 'co2mvt',
    genres: ['Shooter', 'Tactical'],
    franchise: null,
    year: 2020,
    recipes: [{ launcher: 'riot', type: 'exe', label: 'Riot Client', path: '{ProgramFiles}\\Riot Games\\Riot Client\\RiotClientServices.exe', args: '--launch-product=valorant --launch-patchline=live' }],
  },
  {
    n: 2,
    igdbId: 1905,
    name: 'Fortnite',
    summary: 'Drop in, build, and be the last one standing.',
    coverImageId: 'co2ekt',
    genres: ['Shooter', 'Adventure'],
    franchise: null,
    year: 2017,
    recipes: [{ launcher: 'epic', type: 'uri', label: 'Epic Games', uri: 'com.epicgames.launcher://apps/Fortnite?action=launch&silent=true' }],
  },
  {
    n: 3,
    igdbId: 1020,
    name: 'Grand Theft Auto V',
    summary: 'Three criminals, one city.',
    coverImageId: 'co2lbd',
    genres: ['Shooter', 'Adventure'],
    franchise: 'Grand Theft Auto',
    year: 2013,
    recipes: [steam(271590), { launcher: 'epic', type: 'uri', label: 'Epic Games', uri: 'com.epicgames.launcher://apps/9d2d0eb64d5c44529cece33fe2a46482?action=launch&silent=true' }],
  },
  { n: 4, igdbId: 125174, name: 'Overwatch 2', summary: 'Team-based action with a roster of heroes.', coverImageId: 'co5tkm', genres: ['Shooter'], franchise: 'Overwatch', year: 2022, recipes: [{ launcher: 'battlenet', type: 'uri', label: 'Battle.net', uri: 'battlenet://Pro' }] },
  { n: 5, igdbId: 7360, name: 'Tom Clancy’s Rainbow Six Siege', summary: 'Close-quarters tactical team play.', coverImageId: 'co5cvh', genres: ['Shooter', 'Tactical'], franchise: 'Rainbow Six', year: 2015, recipes: [{ launcher: 'ubisoft', type: 'uri', label: 'Ubisoft Connect', uri: 'uplay://launch/635/0' }] },
  { n: 6, igdbId: 121, name: 'Minecraft', summary: 'Build anything, survive the night.', coverImageId: 'co49x5', genres: ['Adventure', 'Simulator'], franchise: 'Minecraft', year: 2011, recipes: [{ launcher: 'microsoft_store', type: 'store', label: 'Microsoft Store', aumid: 'Microsoft.4297127D64EC6_8wekyb3d8bbwe!Minecraft' }] },
  { n: 7, igdbId: 111469, name: 'Among Us', summary: 'Find the impostor before it is too late.', coverImageId: 'co1uaf', genres: ['Strategy', 'Indie'], franchise: null, year: 2018, recipes: [] },
  // In the catalogue, not yet in this venue's library: what "Add title" offers.
  { n: 8, igdbId: 11198, name: 'Rocket League', summary: 'Soccer, with rocket-powered cars.', coverImageId: 'co5w0w', genres: ['Racing', 'Sport'], franchise: null, year: 2015, recipes: [{ launcher: 'epic', type: 'uri', label: 'Epic Games', uri: 'com.epicgames.launcher://apps/Sugar?action=launch&silent=true' }] },
  { n: 9, igdbId: 114795, name: 'Apex Legends', summary: 'A squad-based battle royale.', coverImageId: 'co1wzo', genres: ['Shooter'], franchise: null, year: 2019, recipes: [{ launcher: 'ea', type: 'lnk', label: 'EA app', path: '{UserProfile}\\Desktop\\Apex Legends.lnk' }] },
  { n: 10, igdbId: 2963, name: 'Dota 2', summary: 'Two teams of five, one ancient each.', coverImageId: 'co6ene', genres: ['MOBA', 'Strategy'], franchise: 'Dota', year: 2013, recipes: [steam(570)] },
  {
    n: 11,
    igdbId: 17000,
    name: 'Stardew Valley',
    summary: 'Inherit a farm and make a life.',
    coverImageId: 'co5ra8',
    genres: ['Simulator', 'Role-playing (RPG)'],
    franchise: null,
    year: 2016,
    recipes: [{ launcher: 'standalone', type: 'exe', label: 'Stardew Valley', path: '{ProgramFiles}\\Stardew Valley\\Stardew Valley.exe', workingDirectory: '{ProgramFiles}\\Stardew Valley' }],
  },
];

function game(seed: CatalogueSeed): Game {
  const at = new Date(Date.parse('2026-09-01T09:00:00.000Z') - seed.n * 86_400_000).toISOString();
  return {
    id: mint('gam', '8', seed.n),
    kind: 'game',
    publisher: null,
    ageRating: null,
    tags: [],
    requiresElevation: false,
    provisioning: null,
    signOutPaths: [],
    archivedAt: null,
    editedFields: [],
    coverVariants: [],
    heroVariants: [],
    igdbId: seed.igdbId,
    name: seed.name,
    summary: seed.summary,
    coverImageId: seed.coverImageId,
    // Our own cached copy on the media CDN (spec/14 §6), as fake-hq-catalogue.ts serves it.
    coverUrl: `https://cdn.clavian.test/catalogue/covers/${seed.igdbId}/${seed.coverImageId}.jpg`,
    iconUrl: null,
    iconSource: null,
    suggestedCategory: null,
    detection: { processNames: [], pathPattern: null, regex: null },
    genres: [...seed.genres],
    franchise: seed.franchise,
    firstReleaseYear: seed.year,
    launchRecipes: [...seed.recipes],
    heroUrl: null,
    heroSource: null,
    logoUrl: null,
    logoSource: null,
    createdAt: at,
    updatedAt: at,
  };
}

/**
 * Two HQ-added apps (spec/14 §6), in the catalogue and not in this venue's library: no IGDB record, a square
 * icon served as the cover, and the kiosk category each suggests — what the "Apps" filter of "Add title" offers.
 */
function app(n: number, name: string, suggestedCategory: string, path: string, processName: string, provisioning: Game['provisioning'] = null): Game {
  const at = new Date(Date.parse('2026-09-20T09:00:00.000Z') + n * 60_000).toISOString();
  const id = mint('gam', '8', n);
  const icon = `https://cdn.clavian.test/catalogue/apps/${id}/${'b'.repeat(64)}.png`;
  return {
    id,
    kind: 'app',
    publisher: null,
    ageRating: null,
    tags: [],
    requiresElevation: false,
    provisioning,
    signOutPaths: [],
    archivedAt: null,
    editedFields: [],
    coverVariants: [],
    heroVariants: [],
    igdbId: null,
    name,
    summary: null,
    coverImageId: null,
    coverUrl: icon,
    iconUrl: icon,
    iconSource: 'steamgriddb',
    suggestedCategory,
    detection: { processNames: [processName], pathPattern: null, regex: null },
    genres: [],
    franchise: null,
    firstReleaseYear: null,
    launchRecipes: [{ launcher: 'standalone', type: 'exe', path }],
    heroUrl: null,
    heroSource: null,
    logoUrl: null,
    logoSource: null,
    createdAt: at,
    updatedAt: at,
  };
}

/** Discord's provisioning recipe as HQ seeds it (F-CAT-09): its per-user installer, run as the player. */
const DISCORD_RECIPE: NonNullable<Game['provisioning']> = {
  kind: 'installer',
  downloadUrl: 'https://discord.com/api/downloads/distributions/app/installers/latest?channel=stable&platform=win&arch=x64',
  allowedHosts: ['discord.com', 'stable.dl2.discordapp.net', 'dl.discordapp.net'],
  signer: 'Discord Inc.',
  silentArgs: '-s',
  installedPath: '{LocalAppData}\\Discord\\Update.exe',
  closeProcesses: ['discord'],
  removeRunValues: ['Discord'],
  installRoot: '{LocalAppData}\\Discord',
  restoreExclude: ['SquirrelSetup.log'],
  firstRunSeconds: 120,
};

const GAMES: readonly Game[] = [
  ...CATALOGUE.map(game),
  app(12, 'Discord', 'voice', '{UserProfile}\\AppData\\Local\\Discord\\Update.exe', 'discord', DISCORD_RECIPE),
  app(13, 'Google Chrome', 'browsers', '{ProgramFiles}\\Google\\Chrome\\Application\\chrome.exe', 'chrome'),
];

/** WhatsApp (F-CAT-09): a Store app, registered for the player when it is on the PC. Only in the `playersApps` library. */
const WHATSAPP: Game = { ...app(14, 'WhatsApp', 'voice', '{UserProfile}\\x.exe', 'whatsapp', { kind: 'store', packageFamilyName: '5319275A.WhatsAppDesktop_cv1g1gvanyjgm' }), launchRecipes: [{ launcher: 'microsoft_store', type: 'store', aumid: '5319275A.WhatsAppDesktop_cv1g1gvanyjgm!App' }] };

/** The stations the fake install matrix reports on (F-CAT-03). */
const INSTALL_CLASS = 'scl_01k5znw4hmfay8vvr14d2pf3aa';
/** The one zone of the fake matrix (PC-01 and PC-02 stand in it; PC-03 in none). */
const INSTALL_ZONE = 'zon_01k5znw4hmfay8vvr14d2pf3ab';
const INSTALL_STATIONS = [
  { stationId: 'stn_01k5znw4hmfay8vvr14d2pf3cc', number: 1, name: 'PC-01', classId: INSTALL_CLASS, zoneId: INSTALL_ZONE, scannedAt: '2026-09-29T10:05:00.000Z', launchers: ['steam'] },
  { stationId: 'stn_01k5znw4hmfay8vvr14d2pf3cd', number: 2, name: 'PC-02', classId: INSTALL_CLASS, zoneId: INSTALL_ZONE, scannedAt: '2026-09-29T10:06:00.000Z', launchers: ['steam'] },
  { stationId: 'stn_01k5znw4hmfay8vvr14d2pf3ce', number: 3, name: 'PC-03', classId: INSTALL_CLASS, zoneId: null, scannedAt: null, launchers: [] },
] as const;

function entry(n: number, offer: Pick<LibraryEntry, 'category' | 'classIds' | 'sortOrder' | 'featured' | 'visible'>): LibraryEntry {
  const at = new Date(Date.parse('2026-09-10T06:00:00.000Z') + n * 3_600_000).toISOString();
  return { id: mint('lib', 'a', n), organizationId: ORGANIZATION_ID, venueId: VENUE_ID, game: GAMES[n] as Game, customApp: null, ...offer, elevationDisabled: false, installForPlayers: false, createdAt: at, updatedAt: at };
}

/** An app's entry for the `playersApps` library (F-CAT-09), under Voice, on every class. */
function appEntry(n: number, title: Game, installForPlayers: boolean): LibraryEntry {
  const at = new Date(Date.parse('2026-09-10T06:00:00.000Z') + n * 3_600_000).toISOString();
  return {
    id: mint('lib', 'a', n),
    organizationId: ORGANIZATION_ID,
    venueId: VENUE_ID,
    game: title,
    customApp: null,
    category: 'voice',
    classIds: [],
    sortOrder: n,
    featured: false,
    visible: true,
    elevationDisabled: false,
    installForPlayers,
    createdAt: at,
    updatedAt: at,
  };
}

/** Eight titles, oldest first as the api lists them: featured, hidden, class-limited, and one with no recipe. */
function seedLibrary(): LibraryEntry[] {
  return [
    entry(0, { category: 'competitive', classIds: [], sortOrder: 0, featured: true, visible: true }),
    entry(1, { category: 'competitive', classIds: [], sortOrder: 10, featured: true, visible: true }),
    entry(2, { category: 'games', classIds: [], sortOrder: 0, featured: false, visible: true }),
    entry(3, { category: 'games', classIds: [STANDARD_PC], sortOrder: 10, featured: false, visible: true }),
    entry(4, { category: 'competitive', classIds: [], sortOrder: 20, featured: false, visible: true }),
    entry(5, { category: 'competitive', classIds: [], sortOrder: 30, featured: false, visible: false }),
    entry(6, { category: 'casual', classIds: [STANDARD_PC], sortOrder: 0, featured: false, visible: true }),
    entry(7, { category: 'casual', classIds: [], sortOrder: 10, featured: false, visible: true }),
  ];
}

/** The organisation's one custom app (spec/13 §9): not in the catalogue, not yet in this venue's library. */
function seedCustomApps(): CustomApp[] {
  return [
    {
      id: mint('app', 'c', 0),
      organizationId: ORGANIZATION_ID,
      name: 'LAN Tournament Client',
      suggestedCategory: 'tools',
      iconUrl: null,
      launchRecipes: [{ launcher: 'standalone', type: 'exe', path: '{ProgramFiles}\\LanClient\\client.exe', args: '--kiosk' }],
      detection: { processNames: ['client'], pathPattern: null, regex: null },
      archivedAt: null,
      createdAt: '2026-09-20T08:00:00.000Z',
      updatedAt: '2026-09-20T08:00:00.000Z',
    },
  ];
}

/**
 * A shift's cash timeline as the server reads it from the ledger (F-OPS-04): the float, its cash sales (the shift's cash
 * takings split into three orders), its counter card and UPI takings, the movements, the closing count and the hand-over,
 * oldest first, each with the running expected drawer — which before the count is the shift's `expectedCash`.
 */
function cashTimelineOf(row: Shift, nameOf: (memberId: string | null) => string | null): ShiftCashEvent[] {
  const opened = Date.parse(row.openedAt);
  const ends = row.closedAt === null ? Date.parse(NOW) : Date.parse(row.closedAt);
  const at = (share: number) => new Date(opened + Math.round((ends - opened) * share)).toISOString();
  const by = (memberId: string | null) => (memberId === null ? null : { memberId, name: nameOf(memberId) });
  const base = (id: string, kind: ShiftCashEvent['kind'], when: string): ShiftCashEvent => ({
    id,
    kind,
    at: when,
    tender: null,
    amount: null,
    drawerChange: inr(0),
    balance: inr(0),
    by: null,
    saleId: null,
    orderNumber: null,
    reason: null,
    otherShiftId: null,
    expected: null,
    counted: null,
    variance: null,
    cashReceived: null,
    changeGiven: null,
  });
  const seq = Number.parseInt(row.id.slice(-2), 32) || 0;
  const cash = row.cashSales.amount;
  const sales = [Math.round(cash * 0.4), Math.round(cash * 0.35), cash - Math.round(cash * 0.4) - Math.round(cash * 0.35)];
  const totalChange = changeOf(cash);
  const changes = [Math.floor((totalChange * 0.4) / 100) * 100, Math.floor((totalChange * 0.35) / 100) * 100];
  const changeFor = [changes[0] ?? 0, changes[1] ?? 0, totalChange - (changes[0] ?? 0) - (changes[1] ?? 0)];
  const card = row.tenders.find((count) => count.tender === 'counter_card')?.expected.amount ?? 0;
  const upi = row.tenders.find((count) => count.tender === 'counter_upi')?.expected.amount ?? 0;
  const events: ShiftCashEvent[] = [
    { ...base(`${row.id}-float`, 'opening_float', row.openedAt), amount: row.openingFloat, drawerChange: row.openingFloat, by: by(row.openedByMemberId), otherShiftId: row.carriedFromShiftId },
    ...sales.map((amount, index) => ({
      ...base(`${row.id}-sale-${index}`, 'sale', at(0.15 + index * 0.25)),
      tender: 'cash' as const,
      amount: inr(amount),
      drawerChange: inr(amount),
      cashReceived: inr(amount + (changeFor[index] ?? 0)),
      changeGiven: inr(changeFor[index] ?? 0),
      by: by(row.openedByMemberId),
      saleId: mint('sal', '6', (seq * 4 + index) % 1024),
      orderNumber: 400 + seq * 4 + index + 1,
    })),
    ...([['counter_card', card, 0.3], ['counter_upi', upi, 0.55]] as const)
      .filter(([, amount]) => amount > 0)
      .map(([tender, amount, share], index) => ({
        ...base(`${row.id}-${tender}`, 'sale' as const, at(share)),
        tender,
        amount: inr(amount),
        by: by(row.openedByMemberId),
        saleId: mint('sal', '7', (seq * 4 + index) % 1024),
        orderNumber: 600 + seq * 4 + index + 1,
      })),
    ...row.movements.map((one) => {
      const signed = one.kind === 'pay_in' ? one.amount.amount : -one.amount.amount;
      return { ...base(one.id, one.kind, one.createdAt), amount: inr(signed), drawerChange: inr(signed), by: by(one.recordedByMemberId), reason: one.reason };
    }),
  ];
  if (row.status === 'closed' && row.closedAt !== null && row.countedCash !== null && row.cashVariance !== null) {
    events.push({
      ...base(`${row.id}-count`, 'closing_count', row.closedAt),
      amount: row.countedCash,
      drawerChange: row.cashVariance,
      by: by(row.closedByMemberId),
      expected: row.expectedCash,
      counted: row.countedCash,
      variance: row.cashVariance,
    });
    if (row.carriedToShiftId !== null) {
      events.push({ ...base(`${row.id}-handed`, 'handed_over', row.closedAt), amount: inr(-row.countedCash.amount), drawerChange: inr(-row.countedCash.amount), by: by(row.closedByMemberId), otherShiftId: row.carriedToShiftId });
    }
  }
  const rank = (event: ShiftCashEvent) => (event.kind === 'opening_float' ? 0 : event.kind === 'closing_count' ? 2 : event.kind === 'handed_over' ? 3 : 1);
  events.sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || rank(a) - rank(b));
  let balance = 0;
  return events.map((event) => {
    balance += event.drawerChange.amount;
    return { ...event, balance: inr(balance) };
  });
}

// -------------------------------------------------------------------------------------------------
// The handler
// -------------------------------------------------------------------------------------------------

/** A page of `rows` from an offset cursor, in the one collection envelope (spec/08 §2.3). */
function paged<Row>(rows: readonly Row[], cursor: string | undefined, limit: number) {
  const start = cursor === undefined ? 0 : Number(cursor) || 0;
  const data = rows.slice(start, start + limit);
  const next = start + limit < rows.length ? String(start + limit) : null;
  return { data, page: { cursor: next, hasMore: next !== null, count: data.length } };
}

const addDays = (iso: string, days: number) => new Date(Date.parse(iso) + days * 86_400_000).toISOString();

export function createFakeOps(helpers: VenueOpsHelpers) {
  const { json, problem } = helpers;

  const openShift = seedOpenShift(helpers.drawers === 'mine' ? ME : CASHIER);
  const pass = seedPassProducts();
  const state = {
    /** How many times the venue asked HQ for a title (F-CAT-08). */
    catalogueRequests: 0,
    /** Every shift, newest first; at most one is open. */
    shifts: [
      ...(helpers.drawers === 'two'
        ? [
            shift({
              n: 9,
              counterName: 'Counter 2',
              businessDay: BUSINESS_DAY,
              openedBy: AMAN,
              openedAt: '2026-09-26T12:30:00.000Z',
              openingFloat: 200_000,
              cashSales: 318_000,
              card: 120_000,
              upi: 245_000,
            }),
          ]
        : []),
      openShift,
      ...seedClosedShifts(),
    ] as Shift[],
    /** The venue's counters (ADR-0032): the front desk (its drawer open) and the bar (free). */
    counters: [
      { id: mint('cnt', '7', 0), venueId: VENUE_ID, name: 'Front desk', defaultFloat: inr(500_000), stationId: null, archived: false, archivedAt: null, lastClosingCount: inr(412_000), createdAt: '2026-09-01T06:00:00.000Z', updatedAt: '2026-09-01T06:00:00.000Z' },
      { id: mint('cnt', '7', 1), venueId: VENUE_ID, name: 'Bar', defaultFloat: inr(200_000), stationId: null, archived: false, archivedAt: null, lastClosingCount: null, createdAt: '2026-09-01T06:00:00.000Z', updatedAt: '2026-09-01T06:00:00.000Z' },
    ] as Omit<Counter, 'running'>[],
    /** The venue's closed business days, by day (ADR-0029: closed by staff, or at the rollover). */
    closes: {} as Record<string, BusinessDayClose>,
    products: pass,
    holdings: [
      holding(0, pass[1] as PassProduct, PLAYER, inr(15_000), '2026-09-20T12:10:00.000Z', 3_600),
      holding(1, pass[0] as PassProduct, PLAYER, inr(6_000), '2026-09-26T09:05:00.000Z', 3_600),
    ] as PassHolding[],
    library: [
      ...seedLibrary(),
      ...(helpers.playersApps === true
        ? [appEntry(20, GAMES.find((title) => title.name === 'Discord') as Game, true), appEntry(21, WHATSAPP, false)]
        : []),
    ],
    customApps: seedCustomApps(),
    /** The station ids a `…/library/rescan` asked to rescan, in order (F-CAT-03). */
    rescans: [] as string[],
    /** The entry ids a `…/library/{entryId}/install` asked every PC missing it to install, in order (ADR-0056). */
    installRequests: [] as string[],
    minted: 32,
  };

  const next = (prefix: string, series: string) => mint(prefix, series, state.minted++);

  const invalid = (error: z.ZodError) =>
    problem(400, 'validation_failed', 'Validation failed', {
      detail: 'The request could not be understood as written.',
      errors: error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
    });
  const notFound = (title: string) => problem(404, 'not_found', title);
  const conflict = (detail: string) => problem(409, 'conflict', 'Conflict', { detail });
  const methodNotAllowed = () => problem(405, 'method_not_allowed', 'Method not allowed');

  /** The drawers open now, oldest first (ADR-0029: several counters at once). */
  const openDrawers = () => state.shifts.filter((row) => row.status === 'open').reverse();
  const onDrawer = (row: Shift, member: string) => row.members.some((one) => one.memberId === member && one.leftAt === null);
  /** The signed-in member's drawer, else the one drawer open. */
  const current = () => {
    const open = openDrawers();
    return open.find((row) => onDrawer(row, ME)) ?? (open.length === 1 ? open[0] : undefined);
  };
  const cashHandling = () => {
    const value = helpers.setting?.('shifts.cashHandling');
    return value === 'shared_drawer' || value === 'floor_without_drawer' ? value : 'per_drawer';
  };

  /**
   * The review's totals (spec/13 §10): the day's tenders from its shifts, fixed corrections, and categories and a
   * reconciliation that agree with the revenue exactly — as the server's `dayRevenueBridgeOf` guarantees.
   */
  function dayTotals(today: readonly Shift[]) {
    const sum = (pick: (row: Shift) => number) => today.reduce((total, row) => total + pick(row), 0);
    const tender = (name: ShiftTenderCount['tender']) => (row: Shift) => row.tenders.find((count) => count.tender === name)?.expected.amount ?? 0;
    const cash = sum((row) => row.cashSales.amount);
    const card = sum(tender('counter_card'));
    const upi = sum(tender('counter_upi'));
    const walletTender = 45_000;
    const walletTopUps = 100_000;
    const walletBonus = 5_000;
    const refunds = 25_000;
    const discounts = 12_000;
    const onAccount = 30_000;
    // M-01 (F-ANL-01): the counter tenders less the ₹1,000 of top-ups in the drawer (not revenue), plus the wallet
    // money spent — ₹450 of wallet payments, of which ₹50 was bonus credit (not money).
    const revenue = cash + card + upi - walletTopUps + (walletTender - walletBonus);
    // What was sold, ex-tax and before discounts, at this untaxed venue: revenue plus what the bridge takes off.
    const sales = revenue + discounts + refunds + onAccount + walletBonus;
    const food = Math.round(sales * 0.35);
    const retail = 42_000;
    return {
      // The takings the ledger holds by tender: the day's cash sales, the counter card and UPI, and the wallet.
      tenders: [
        { tender: 'cash' as const, amount: inr(cash) },
        { tender: 'counter_card' as const, amount: inr(card) },
        { tender: 'counter_upi' as const, amount: inr(upi) },
        { tender: 'wallet' as const, amount: inr(walletTender) },
      ],
      categories: [
        { category: 'play' as const, amount: inr(sales - food - retail) },
        { category: 'food' as const, amount: inr(food) },
        { category: 'retail' as const, amount: inr(retail) },
        { category: 'other' as const, amount: inr(0) },
      ],
      refunds: { count: 1, amount: inr(refunds) },
      voids: { count: 2, amount: inr(18_000) },
      discounts: inr(discounts),
      comps: { sessions: 0, seconds: 0 },
      receivablesRaised: inr(onAccount),
      passSales: inr(640_000),
      walletTopUps: inr(walletTopUps),
      walletCreditGiven: inr(10_000),
      totalCashVariance: inr(sum((row) => row.cashVariance?.amount ?? 0)),
      revenue: inr(revenue),
      revenueBridge: {
        sales: inr(sales),
        discounts: inr(discounts),
        tax: inr(0),
        refunds: inr(refunds),
        onAccount: inr(onAccount),
        walletBonus: inr(walletBonus),
        walletSettling: inr(0),
        taxPaidAtTopUp: inr(0),
        other: inr(0),
        revenue: inr(revenue),
      },
      // Money in: the counter tenders (top-ups included). Revenue = money in − top-ups + wallet cash spent (₹400).
      moneyIn: inr(cash + card + upi),
      walletSpent: inr(walletTender - walletBonus),
      bonusSpent: inr(walletBonus),
    };
  }

  const dayCloseMode = () => (helpers.setting?.('shifts.dayCloseMode') === 'auto' ? 'auto' : 'manual');
  const shiftsEnabled = () => helpers.setting?.('shifts.enabled') !== false;
  const memberName = staffName;
  const custodyOf = (open: Shift) => ({
    shiftId: open.id,
    counterName: open.counterName,
    openedByMemberId: open.openedByMemberId,
    openedByName: memberName(open.openedByMemberId),
    openedAt: open.openedAt,
    openingFloat: open.openingFloat,
    businessDay: open.businessDay,
    members: open.members.filter((member) => member.leftAt === null).map((member) => ({ memberId: member.memberId, name: memberName(member.memberId) })),
  });

  /** At an automatic venue the rollover has closed yesterday by itself (ADR-0029), as the worker would have. */
  function rollOver(): void {
    if (dayCloseMode() !== 'auto' || state.closes[PREVIOUS_DAY] !== undefined) return;
    state.closes[PREVIOUS_DAY] = {
      id: next('bdc', 'd'),
      venueId: VENUE_ID,
      businessDay: PREVIOUS_DAY,
      closedByMemberId: null,
      closedAt: '2026-09-26T00:35:00.000Z',
      ...dayTotals(state.shifts.filter((row) => row.businessDay === PREVIOUS_DAY)),
      note: null,
      closeMode: 'auto',
      signedOffByMemberId: null,
      signedOffAt: null,
      signOffNote: null,
    };
  }

  function dayCloseReview(day: string = BUSINESS_DAY): DayCloseReview {
    rollOver();
    const today = state.shifts.filter((row) => row.businessDay === day);
    const open = today.filter((row) => row.status === 'open').length;
    const close = state.closes[day];
    return {
      venueId: VENUE_ID,
      businessDay: day,
      closed: close !== undefined,
      allShiftsClosed: open === 0,
      openShiftCount: open,
      ...dayTotals(today),
      shifts: today.map((row) => ({
        id: row.id,
        status: row.status,
        counterName: row.counterName,
        openedByMemberId: row.openedByMemberId,
        openedByName: memberName(row.openedByMemberId),
        openedAt: row.openedAt,
        closedByMemberId: row.closedByMemberId,
        closedAt: row.closedAt,
        expectedCash: row.status === 'closed' ? row.expectedCash : null,
        countedCash: row.countedCash,
        cashVariance: row.cashVariance,
        varianceAlerted: row.varianceAlerted,
      })),
      closedByMemberId: close?.closedByMemberId ?? null,
      closedAt: close?.closedAt ?? null,
      closeMode: close?.closeMode ?? null,
      signedOffByMemberId: close?.signedOffByMemberId ?? null,
      signedOffAt: close?.signedOffAt ?? null,
    };
  }

  /** `GET …/business-day` (ADR-0029): the day now, its rollover and mode, the drawer's holder, the days to act on. */
  function businessDayStatus() {
    rollOver();
    const open = current();
    return {
      venueId: VENUE_ID,
      businessDay: BUSINESS_DAY,
      startedAt: '2026-09-26T00:30:00.000Z',
      rollsOverAt: '2026-09-27T00:30:00.000Z',
      cutoff: '06:00',
      timezone: 'Asia/Kolkata',
      dayCloseMode: dayCloseMode(),
      shiftsEnabled: shiftsEnabled(),
      previousDayClosed: state.closes[PREVIOUS_DAY] !== undefined,
      awaitingSignOff: Object.values(state.closes)
        .filter((close) => close.closeMode === 'auto' && close.signedOffAt === null)
        .map((close) => close.businessDay)
        .sort()
        .reverse(),
      custody: open === undefined ? null : custodyOf(open),
      drawers: openDrawers().map(custodyOf),
      cashHandling: cashHandling(),
      // shifts.blindClose (on by default) and shifts.varianceNoteAmount (₹100).
      blindClose: helpers.setting?.('shifts.blindClose') !== false,
      varianceNoteAmount: inr(10_000),
    };
  }

  /** A drawer open now, as the running counters panel reads it (ADR-0032): money to whom may see it. */
  function runningOf(row: Shift): RunningDrawer {
    const visible = helpers.can === undefined || helpers.can('shifts:read_all') || onDrawer(row, ME);
    const counter = state.counters.find((one) => one.name.toLowerCase() === (row.counterName ?? '').toLowerCase());
    return {
      shiftId: row.id,
      counterId: counter?.id ?? null,
      counterName: row.counterName,
      openedAt: row.openedAt,
      businessDay: row.businessDay,
      openedByMemberId: row.openedByMemberId,
      members: row.members.map((member, index) => ({
        memberId: member.memberId,
        name: memberName(member.memberId),
        joinedAt: member.joinedAt,
        leftAt: member.leftAt,
        cash: visible ? (index === 0 ? row.cashSales : inr(0)) : null,
        salesCount: visible ? (index === 0 ? 4 : 0) : null,
      })),
      figuresVisible: visible,
      openingFloat: visible ? row.openingFloat : null,
      cashSoFar: visible ? row.cashSales : null,
      expectedDrawer: visible ? row.expectedCash : null,
      otherCash: visible ? inr(0) : null,
    };
  }

  /** `GET …/counters` (ADR-0032). */
  function counterBoard(includeArchived: boolean): CounterBoard {
    const open = openDrawers();
    const runningAt = (name: string) => open.find((row) => (row.counterName ?? '').toLowerCase() === name.toLowerCase());
    return {
      counters: state.counters
        .filter((counter) => includeArchived || !counter.archived)
        .map((counter) => {
          const row = runningAt(counter.name);
          return { ...counter, running: row === undefined ? null : runningOf(row) };
        }),
      unassigned: open.filter((row) => !state.counters.some((counter) => counter.name.toLowerCase() === (row.counterName ?? '').toLowerCase())).map(runningOf),
    };
  }

  function countersRoute(method: string, rest: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    if (rest === '') {
      if (method === 'GET') return () => json(200, counterBoard(query.get('includeArchived') === 'true'));
      if (method === 'POST') {
        return () => {
          if (helpers.can !== undefined && !helpers.can('settings:payments')) return problem(403, 'forbidden', 'Forbidden');
          const name = String(input.name ?? '').trim();
          if (name === '') return problem(400, 'validation_failed', 'Validation failed');
          if (state.counters.some((one) => one.name.toLowerCase() === name.toLowerCase())) return conflict('A counter with that name already exists here (it may be archived — restore it instead).');
          const made = {
            id: next('cnt', '7'),
            venueId: VENUE_ID,
            name,
            defaultFloat: (input.defaultFloat as Money | undefined) ?? inr(0),
            stationId: (input.stationId as string | undefined) ?? null,
            archived: false,
            archivedAt: null,
            lastClosingCount: null,
            createdAt: NOW,
            updatedAt: NOW,
          };
          state.counters.push(made);
          return json(201, { ...made, running: null });
        };
      }
      return methodNotAllowed;
    }
    const id = rest.slice(1);
    const found = state.counters.find((one) => one.id === id);
    if (method !== 'PATCH') return methodNotAllowed;
    return () => {
      if (found === undefined) return notFound('No such counter at this venue.');
      if (helpers.can !== undefined && !helpers.can('settings:payments')) return problem(403, 'forbidden', 'Forbidden');
      const open = openDrawers().find((row) => (row.counterName ?? '').toLowerCase() === found.name.toLowerCase());
      if (input.archived === true && open !== undefined) return conflict('A drawer is open at this counter. End the shift or hand it over before archiving it.');
      const changed = {
        ...found,
        ...(typeof input.name === 'string' ? { name: input.name.trim() } : {}),
        ...(input.defaultFloat !== undefined ? { defaultFloat: input.defaultFloat as Money } : {}),
        ...(input.stationId !== undefined ? { stationId: input.stationId as string | null } : {}),
        ...(typeof input.archived === 'boolean' ? { archived: input.archived, archivedAt: input.archived ? NOW : null } : {}),
        updatedAt: NOW,
      };
      state.counters = state.counters.map((one) => (one.id === id ? changed : one));
      return json(200, { ...changed, running: null });
    };
  }

  function openNew(openingFloat: Money, counterName: string | null, carriedFromShiftId: string | null): Shift {
    const opened: Shift = {
      ...shift({ n: 0, counterName, businessDay: BUSINESS_DAY, openedBy: ME, openedAt: NOW, openingFloat: openingFloat.amount, cashSales: 0, card: 0, upi: 0 }),
      id: next('shf', '4'),
      carriedFromShiftId,
    };
    state.shifts.unshift(opened);
    return opened;
  }

  // -- Shifts ---------------------------------------------------------------------------------

  function shifts(method: string, rest: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    if (rest === '') {
      if (method === 'GET') {
        return () => {
          const status = query.get('status');
          const day = query.get('businessDay');
          // F-OPS-07: the alerted variances whose business day is not closed yet.
          const alerted = query.get('varianceAlerted') === 'true';
          const dayClosed = (row: Shift) => state.closes[row.businessDay] !== undefined;
          return json(200, {
            shifts: state.shifts.filter(
              (row) => (status === null || row.status === status) && (day === null || row.businessDay === day) && (!alerted || (row.varianceAlerted && !dayClosed(row))),
            ),
          });
        };
      }
      if (method === 'POST') {
        return () => {
          const parsed = OpenShiftSchema.safeParse(input);
          if (!parsed.success) return invalid(parsed.error);
          if (!shiftsEnabled()) return conflict('This venue does not track cash drawers (shifts.enabled is off), so no shift starts.');
          const open = openDrawers();
          if (cashHandling() === 'shared_drawer' && open.length > 0) return conflict('This venue shares one drawer, and it is open. Join it instead.');
          if (open.some((row) => onDrawer(row, ME))) return conflict('You already hold a drawer. End it or hand it over first.');
          // ADR-0032: a counter of the venue's list names the drawer.
          const counter = parsed.data.counterId === undefined ? undefined : state.counters.find((one) => one.id === parsed.data.counterId);
          if (parsed.data.counterId !== undefined && counter === undefined) return conflict('No such counter at this venue. Pick one from the list.');
          if (counter?.archived === true) return conflict(`${counter.name} is archived. Restore it in Settings → Counters, or pick another counter.`);
          const counterName = counter?.name ?? parsed.data.counterName;
          const key = (name: string | null | undefined) => (name ?? '').trim().toLowerCase();
          const taken = open.find((row) => key(row.counterName) === key(counterName));
          if (taken !== undefined) return conflict(`A drawer is already open at ${taken.counterName ?? 'this counter'}. Name the counter you are opening, or take that drawer over.`);
          return json(201, openNew(parsed.data.openingFloat, counterName ?? null, parsed.data.carriedFromShiftId ?? null));
        };
      }
      return methodNotAllowed;
    }
    if (rest === '/current') {
      if (method !== 'GET') return methodNotAllowed;
      return () => {
        const open = current();
        return open === undefined ? notFound('No shift is open at this venue.') : json(200, open);
      };
    }
    const match = /^\/([^/]+)(\/movements|\/close|\/join|\/leave|\/cash-timeline)?$/.exec(rest);
    if (match === null) return undefined;
    const [, shiftId, action] = match;
    const find = () => state.shifts.find((row) => row.id === shiftId);
    // F-OPS-04: the shift's cash timeline, filtered (cash only by default, or every tender; one order) and paged.
    if (action === '/cash-timeline') {
      if (method !== 'GET') return methodNotAllowed;
      return () => {
        const parsed = ShiftCashTimelineQuerySchema.safeParse(Object.fromEntries(query.entries()));
        if (!parsed.success) return invalid(parsed.error);
        const found = find();
        // `shifts:own` reads a shift the caller opened or was on; any other needs `shifts:read_all` (the server's rule).
        const own = found !== undefined && (found.openedByMemberId === ME || found.members.some((member) => member.memberId === ME));
        if (helpers.can !== undefined && !helpers.can('shifts:read_all') && !own) {
          return problem(403, 'forbidden', 'Forbidden', { detail: 'This shift is another member’s. Reading its cash timeline needs the shifts:read_all permission.' });
        }
        if (found === undefined) return notFound('No such shift at this venue.');
        const listed = cashTimelineOf(found, memberName).filter(
          (event) => (parsed.data.tenders === 'all' || event.tender === null || event.tender === 'cash') && (parsed.data.orderNumber === undefined || event.orderNumber === parsed.data.orderNumber),
        );
        return json(200, paged(listed, parsed.data.cursor, parsed.data.limit));
      };
    }
    // ADR-0029: a colleague joins a shared drawer, and leaves it while someone else stays on it.
    if ((action === '/join' || action === '/leave') && method === 'POST') {
      return () => {
        const found = find();
        if (found === undefined) return notFound('No such shift at this venue.');
        if (found.status !== 'open') return conflict('The shift is closed.');
        const active = found.members.filter((member) => member.leftAt === null);
        if (action === '/join') {
          if (cashHandling() !== 'shared_drawer') return conflict('This venue does not share a drawer (shifts.cashHandling): each person holds their own.');
          if (openDrawers().some((row) => onDrawer(row, ME))) return conflict('You already hold a drawer. End it or hand it over first.');
          const joined: Shift = { ...found, members: [...found.members, { memberId: ME, joinedAt: NOW, leftAt: null }] };
          state.shifts = state.shifts.map((row) => (row.id === found.id ? joined : row));
          return json(200, joined);
        }
        if (!active.some((member) => member.memberId === ME)) return conflict('You are not on this drawer.');
        if (active.length <= 1) return conflict('You are the last one on this drawer. End the shift with a count, or hand it over.');
        const left: Shift = { ...found, members: found.members.map((member) => (member.memberId === ME && member.leftAt === null ? { ...member, leftAt: NOW } : member)) };
        state.shifts = state.shifts.map((row) => (row.id === found.id ? left : row));
        return json(200, left);
      };
    }
    if (action === undefined) {
      if (method !== 'GET') return methodNotAllowed;
      return () => {
        const found = find();
        return found === undefined ? notFound('No such shift at this venue.') : json(200, found);
      };
    }
    if (method !== 'POST') return methodNotAllowed;
    if (action === '/movements') {
      return () => {
        const parsed = RecordCashMovementSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const found = find();
        if (found === undefined) return notFound('No such shift at this venue.');
        if (found.status !== 'open') return conflict('The shift is closed.');
        const movements = [
          ...found.movements,
          { id: next('cmv', '5'), kind: parsed.data.kind, amount: parsed.data.amount, reason: parsed.data.reason, recordedByMemberId: CASHIER, createdAt: NOW },
        ];
        const expected = expectedCashOf(found.openingFloat.amount, found.cashSales.amount, movements);
        const updated: Shift = {
          ...found,
          movements,
          expectedCash: inr(expected),
          tenders: found.tenders.map((count) => (count.tender === 'cash' ? { ...count, expected: inr(expected) } : count)),
        };
        state.shifts = state.shifts.map((row) => (row.id === found.id ? updated : row));
        return json(200, updated);
      };
    }
    return () => {
      const parsed = CloseShiftSchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = find();
      if (found === undefined) return notFound('No such shift at this venue.');
      if (found.status !== 'open') return conflict('This shift is already closed.');
      const body = parsed.data;
      const variance = body.countedCash.amount - found.expectedCash.amount;
      // `shifts.varianceNoteAmount` defaults to ₹100 (10000 paise): past it, a note is required.
      if (Math.abs(variance) > 10_000 && (body.note === undefined || body.note.trim() === '')) {
        return conflict('A note is required for this variance (shifts.varianceNoteAmount).');
      }
      const countedFor = (tender: ShiftTenderCount['tender']): Money | undefined =>
        tender === 'cash' ? body.countedCash : tender === 'counter_card' ? body.countedCounterCard : body.countedCounterUpi;
      let closed: Shift = {
        ...found,
        status: 'closed',
        closedByMemberId: CASHIER,
        closedAt: NOW,
        closingNote: body.note ?? null,
        countBusinessDay: BUSINESS_DAY,
        members: found.members.map((member) => (member.leftAt === null ? { ...member, leftAt: NOW } : member)),
        reportKind: 'z',
        countedCash: body.countedCash,
        cashVariance: inr(variance),
        varianceAlerted: Math.abs(variance) > 50_000,
        tenders: found.tenders.map((count) => {
          const counted = countedFor(count.tender);
          return counted === undefined ? count : { ...count, counted, variance: inr(counted.amount - count.expected.amount) };
        }),
      };
      state.shifts = state.shifts.map((row) => (row.id === found.id ? closed : row));
      if (body.handOver === true) {
        const opened = openNew(body.countedCash, found.counterName, found.id);
        closed = { ...closed, carriedToShiftId: opened.id };
        state.shifts = state.shifts.map((row) => (row.id === found.id ? closed : row));
      }
      return json(200, closed);
    };
  }

  function dayClose(method: string, input: Record<string, unknown>, query: URLSearchParams): () => FakeResponse {
    if (method === 'GET') return () => json(200, dayCloseReview(query.get('businessDay') ?? BUSINESS_DAY));
    if (method !== 'POST') return methodNotAllowed;
    return () => {
      const parsed = CloseBusinessDaySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      if (state.closes[parsed.data.businessDay] !== undefined) return conflict('This business day is already closed.');
      const review = dayCloseReview(parsed.data.businessDay);
      if (!review.allShiftsClosed) {
        // The server names each open shift and its cashier (F-OPS-06).
        const open = review.shifts.filter((row) => row.status === 'open');
        const named = open.map((row) => `${row.counterName ?? `Shift ${row.id}`} (${row.id}), opened by ${row.openedByName ?? row.openedByMemberId ?? 'someone'}`);
        return conflict(`The day cannot close while a shift is open. ${named.join('; ')} is still open. Close it first.`);
      }
      const closed: BusinessDayClose = {
        id: next('bdc', 'd'),
        venueId: VENUE_ID,
        businessDay: parsed.data.businessDay,
        closedByMemberId: OWNER,
        closedAt: NOW,
        ...dayTotals(state.shifts.filter((row) => row.businessDay === parsed.data.businessDay)),
        note: parsed.data.note ?? null,
        closeMode: 'manual',
        signedOffByMemberId: null,
        signedOffAt: null,
        signOffNote: null,
      };
      state.closes[parsed.data.businessDay] = closed;
      return json(200, closed);
    };
  }

  /** `POST …/day-close/sign-off` (ADR-0029): a manager signs off a day that closed at its rollover. */
  function signOff(method: string, input: Record<string, unknown>): () => FakeResponse {
    if (method !== 'POST') return methodNotAllowed;
    return () => {
      const parsed = SignOffBusinessDaySchema.safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const close = state.closes[parsed.data.businessDay];
      if (close === undefined) return conflict('This business day is not closed yet.');
      if (close.closeMode !== 'auto') return conflict('Staff closed this business day; that close was its sign-off.');
      if (close.signedOffAt !== null) return conflict('This business day is already signed off.');
      const signed: BusinessDayClose = { ...close, signedOffByMemberId: OWNER, signedOffAt: NOW, signOffNote: parsed.data.note ?? null };
      state.closes[parsed.data.businessDay] = signed;
      return json(200, signed);
    };
  }

  // -- Passes ---------------------------------------------------------------------------------

  function passes(method: string, rest: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    if (rest === '/products') {
      if (method === 'GET') {
        return () => {
          const parsed = PageQuerySchema.safeParse(Object.fromEntries(query));
          if (!parsed.success) return invalid(parsed.error);
          const sorted = [...state.products].sort((a, b) => a.sortOrder - b.sortOrder);
          return json(200, paged(sorted, parsed.data.cursor, parsed.data.limit));
        };
      }
      if (method !== 'POST') return methodNotAllowed;
      return () => {
        const parsed = CreatePassProductRequestSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const { active, sortOrder, accentToken, ...terms } = parsed.data;
        const id = next('ppr', '6');
        const created: PassProduct = {
          id,
          version: { ...terms, id: next('ppv', '7'), productId: id, version: 1, createdAt: NOW },
          active,
          sortOrder,
          accentToken: accentToken ?? null,
          soldToday: 0,
          createdAt: NOW,
          updatedAt: NOW,
        };
        state.products.push(created);
        return json(201, created);
      };
    }
    if (rest === '/holdings') {
      if (method !== 'GET') return methodNotAllowed;
      return () => {
        const parsed = PassHoldingQuerySchema.safeParse(Object.fromEntries(query));
        if (!parsed.success) return invalid(parsed.error);
        const { productId, playerId, status, cursor, limit } = parsed.data;
        const rows = state.holdings.filter(
          (row) => (productId === undefined || row.productId === productId) && (playerId === undefined || row.playerId === playerId) && (status === undefined || row.status === status),
        );
        return json(200, paged(rows, cursor, limit));
      };
    }
    const match = /^\/products\/([^/]+)(\/sell|\/grant)?$/.exec(rest);
    if (match === null) return undefined;
    const [, productId, action] = match;
    const find = () => state.products.find((row) => row.id === productId);
    if (action === undefined) {
      if (method === 'GET') {
        return () => {
          const found = find();
          return found === undefined ? notFound('No such pass product.') : json(200, found);
        };
      }
      if (method !== 'PATCH') return undefined;
      // Edit (F-PRC-01): a term change mints a new version; active/sortOrder/accentToken change in place.
      return () => {
        const parsed = UpdatePassProductRequestSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const found = find();
        if (found === undefined) return notFound('No such pass product.');
        const { active, sortOrder, accentToken, ...terms } = parsed.data;
        const version =
          Object.keys(terms).length > 0
            ? { ...found.version, ...terms, id: next('ppv', '7'), version: found.version.version + 1, createdAt: NOW }
            : found.version;
        const updated: PassProduct = {
          ...found,
          version,
          active: active ?? found.active,
          sortOrder: sortOrder ?? found.sortOrder,
          accentToken: accentToken === undefined ? found.accentToken : accentToken,
          updatedAt: NOW,
        };
        state.products = state.products.map((row) => (row.id === found.id ? updated : row));
        return json(200, updated);
      };
    }
    if (method !== 'POST') return methodNotAllowed;
    return () => {
      const parsed = (action === '/sell' ? SellPassRequestSchema : GrantPassRequestSchema).safeParse(input);
      if (!parsed.success) return invalid(parsed.error);
      const found = find();
      if (found === undefined) return notFound('No such pass product.');
      if (!found.active) return conflict('This pass is off sale.');
      const sold = holding(0, found, parsed.data.playerId, action === '/sell' ? found.version.price : inr(0), NOW, found.version.durationSeconds);
      const created: PassHolding = {
        ...sold,
        id: next('pah', 'h'),
        expiresAt: found.version.validity.type === 'days_after_purchase' ? addDays(NOW, found.version.validity.days ?? 0) : sold.expiresAt,
      };
      state.holdings.unshift(created);
      state.products = state.products.map((row) => (row.id === found.id ? { ...row, soldToday: row.soldToday + 1 } : row));
      return json(201, created);
    };
  }

  // -- The library ----------------------------------------------------------------------------

  function library(method: string, rest: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    // Install detection (F-CAT-03): the titles × stations matrix, the titles found on PCs, and a rescan.
    if (rest === '/installs') return method === 'GET' ? () => json(200, installMatrix()) : methodNotAllowed;
    if (rest === '/detected') return method === 'GET' ? () => json(200, detectedTitles()) : methodNotAllowed;
    // F-CAT-08: a detected title with no catalogue match becomes a request to HQ (one per title).
    if (rest === '/requests') {
      return method === 'POST'
        ? () => {
            const body = input as { source?: string; sourceId?: string; name?: string };
            state.catalogueRequests += 1;
            return json(200, {
              id: 'ctr_01k5znw4hmfay8vvr14d2pf8b0',
              source: body.source ?? 'steam',
              sourceId: body.sourceId ?? '',
              name: body.name ?? '',
              status: 'open',
              requestCount: state.catalogueRequests,
              gameId: null,
              createdAt: NOW,
              lastRequestedAt: NOW,
              resolvedAt: null,
            });
          }
        : methodNotAllowed;
    }
    if (rest === '/rescan') {
      if (method !== 'POST') return methodNotAllowed;
      return () => {
        const wanted = (input['stationIds'] as string[] | undefined) ?? INSTALL_STATIONS.map((station) => station.stationId);
        state.rescans.push(...wanted);
        return json(202, {
          commands: wanted.map((stationId, index) => ({
            id: `cmd_01k5znw4hmfay8vvr14d2pr${String(index).padStart(3, '0')}`,
            stationId,
            type: 'station.library.rescan',
            status: 'queued',
            createdAt: NOW,
            expiresAt: NOW,
          })),
        });
      };
    }
    if (rest === '') {
      if (method === 'GET') {
        return () => {
          const parsed = PageQuerySchema.safeParse(Object.fromEntries(query));
          if (!parsed.success) return invalid(parsed.error);
          return json(200, paged(state.library, parsed.data.cursor, parsed.data.limit));
        };
      }
      if (method !== 'POST') return methodNotAllowed;
      return () => {
        const parsed = AddLibraryEntrySchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        let picked: Game | null = null;
        let custom: CustomApp | null = null;
        if (parsed.data.customAppId !== undefined) {
          custom = state.customApps.find((candidate) => candidate.id === parsed.data.customAppId && candidate.archivedAt === null) ?? null;
          if (custom === null) {
            return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'customAppId', code: 'custom', message: 'No such custom app.' }] });
          }
          const appId = custom.id;
          if (state.library.some((row) => row.customApp?.id === appId)) return conflict('That title is already in this venue’s library.');
        } else {
          picked = GAMES.find((candidate) => candidate.id === parsed.data.gameId) ?? null;
          if (picked === null) {
            return problem(400, 'validation_failed', 'Validation failed', { errors: [{ field: 'gameId', code: 'custom', message: 'No such game in the catalogue.' }] });
          }
          const gameId = picked.id;
          if (state.library.some((row) => row.game?.id === gameId)) return conflict('That game is already in this venue’s library.');
        }
        const created: LibraryEntry = {
          id: next('lib', 'a'),
          organizationId: ORGANIZATION_ID,
          venueId: VENUE_ID,
          game: picked,
          customApp: custom,
          elevationDisabled: false,
          installForPlayers: false,
          category: parsed.data.category,
          classIds: parsed.data.classIds ?? [],
          sortOrder: parsed.data.sortOrder ?? 0,
          featured: parsed.data.featured ?? false,
          visible: parsed.data.visible ?? true,
          createdAt: NOW,
          updatedAt: NOW,
        };
        state.library.push(created);
        return json(201, created);
      };
    }
    // ADR-0056: install one app on every PC missing it — the PCs whose matrix state is not `installed` are asked.
    const install = /^\/([^/]+)\/install$/.exec(rest);
    if (install !== null) {
      if (method !== 'POST') return methodNotAllowed;
      return () => {
        const held = state.library.find((row) => row.id === install[1]);
        if (held === undefined) return notFound('No such library entry.');
        if ((held.game?.provisioning ?? null) === null) return problem(422, 'unprocessable', 'This app has no install recipe.');
        state.installRequests.push(held.id);
        const installed = new Set(
          installMatrix()
            .provisioning.filter((cell) => cell.entryId === held.id && cell.state === 'installed')
            .map((cell) => cell.stationId),
        );
        const asked = INSTALL_STATIONS.filter((station) => !installed.has(station.stationId));
        return json(202, {
          commands: asked.map((station, index) => ({
            id: `cmd_01k5znw4hmfay8vvr14d2pi${String(index).padStart(3, '0')}`,
            stationId: station.stationId,
            type: 'station.app.install',
            status: 'queued',
            createdAt: NOW,
            expiresAt: NOW,
          })),
          skipped: [...installed].map((stationId) => ({ stationId, reason: 'installed' })),
        });
      };
    }
    const match = /^\/([^/]+)$/.exec(rest);
    if (match === null) return undefined;
    const entryId = match[1];
    const found = () => state.library.find((row) => row.id === entryId);
    if (method === 'PATCH') {
      return () => {
        const parsed = UpdateLibraryEntrySchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const held = found();
        if (held === undefined) return notFound('No such library entry.');
        const patch = parsed.data;
        // F-CAT-09: only a catalogue app with a provisioning recipe can be installed for players.
        if (patch.installForPlayers === true && (held.game?.provisioning ?? null) === null) {
          return problem(400, 'validation_failed', 'Validation failed', {
            errors: [{ field: 'installForPlayers', code: 'not-provisionable', message: 'This title has no provisioning recipe.' }],
          });
        }
        const updated: LibraryEntry = {
          ...held,
          ...(patch.category === undefined ? {} : { category: patch.category }),
          ...(patch.classIds === undefined ? {} : { classIds: patch.classIds }),
          ...(patch.sortOrder === undefined ? {} : { sortOrder: patch.sortOrder }),
          ...(patch.featured === undefined ? {} : { featured: patch.featured }),
          ...(patch.visible === undefined ? {} : { visible: patch.visible }),
          ...(patch.installForPlayers === undefined ? {} : { installForPlayers: patch.installForPlayers }),
          updatedAt: NOW,
        };
        state.library = state.library.map((row) => (row.id === held.id ? updated : row));
        return json(200, updated);
      };
    }
    if (method === 'DELETE') {
      return () => {
        const held = found();
        if (held === undefined) return notFound('No such library entry.');
        state.library = state.library.filter((row) => row.id !== held.id);
        return { status: 204, headers: {}, body: '' };
      };
    }
    return methodNotAllowed;
  }

  function catalogue(method: string, query: URLSearchParams): () => FakeResponse {
    if (method !== 'GET') return methodNotAllowed;
    return () => {
      const parsed = BrowseVenueCatalogueQuerySchema.safeParse(Object.fromEntries(query));
      if (!parsed.success) return invalid(parsed.error);
      const q = parsed.data.q?.toLowerCase();
      const kind = parsed.data.kind;
      const rows: VenueCatalogueGame[] = GAMES.filter(
        (candidate) => (q === undefined || candidate.name.toLowerCase().includes(q)) && (kind === undefined || candidate.kind === kind),
      ).map((candidate) => {
        const held = state.library.find((row) => row.game?.id === candidate.id);
        return { game: candidate, inLibrary: held !== undefined, entryId: held?.id ?? null };
      });
      return json(200, paged(rows, parsed.data.cursor, parsed.data.limit));
    };
  }

  /** The matrix over the first three library titles: PC-01 has two (one updating), PC-02 has one, PC-03 never reported. */
  function installMatrix() {
    // The first three titles, and the apps PCs install for players when the `playersApps` library holds them (F-CAT-09).
    const players = state.library.filter((entry) => (entry.game?.provisioning ?? null) !== null);
    const titles = [...state.library.slice(0, 3), ...players.filter((entry) => !state.library.slice(0, 3).includes(entry))];
    const discord = players.find((entry) => entry.game?.name === 'Discord');
    const whatsapp = players.find((entry) => entry.game?.name === 'WhatsApp');
    const said = (station: number, entryId: string, state: string, failure: string | null = null, detail: string | null = null, nextAttemptAt: string | null = null) => ({
      stationId: INSTALL_STATIONS[station]!.stationId,
      entryId,
      state,
      failure,
      detail,
      attempts: failure === null ? 0 : 1,
      nextAttemptAt,
    });
    return {
      classes: [{ classId: INSTALL_CLASS, name: 'Standard PC' }],
      zones: [{ zoneId: INSTALL_ZONE, name: 'Arena floor' }],
      stations: INSTALL_STATIONS,
      titles: titles.map((entry) => ({
        entryId: entry.id,
        name: entry.game?.name ?? entry.customApp?.name ?? '',
        classIds: entry.classIds,
        provisionable: (entry.game?.provisioning ?? null) !== null,
        installForPlayers: entry.installForPlayers,
      })),
      provisioning: [
        ...(discord === undefined
          ? []
          : [
              said(0, discord.id, 'installed'),
              said(1, discord.id, 'failed', 'signer_mismatch', 'signed by Someone Else Ltd', '2026-09-29T10:25:00.000Z'),
            ]),
        ...(whatsapp === undefined ? [] : [said(0, whatsapp.id, 'other_account_only')]),
      ],
      cells: [
        ...(discord === undefined ? [] : [{ stationId: INSTALL_STATIONS[0]!.stationId, entryId: discord.id, updating: null, version: null, unverified: false, sizeBytes: null }]),
        ...(titles[0] === undefined ? [] : [{ stationId: INSTALL_STATIONS[0]!.stationId, entryId: titles[0].id, updating: null, version: '20123456', unverified: false, sizeBytes: 34_359_738_368 }]),
        ...(titles[1] === undefined ? [] : [{ stationId: INSTALL_STATIONS[0]!.stationId, entryId: titles[1].id, updating: { progress: 42 }, version: null, unverified: false, sizeBytes: null }]),
        ...(titles[0] === undefined ? [] : [{ stationId: INSTALL_STATIONS[1]!.stationId, entryId: titles[0].id, updating: null, version: '20123455', unverified: false, sizeBytes: null }]),
      ],
    };
  }

  /** Dota 2 is in the catalogue (not the library) and on two PCs; an indie game is on one PC and not in the catalogue. */
  function detectedTitles() {
    const dota = GAMES.find((candidate) => candidate.name === 'Dota 2');
    return {
      titles: [
        {
          source: 'steam',
          sourceId: '570',
          name: 'Dota 2',
          versions: ['19000001'],
          stations: ['PC 1', 'PC 2'],
          catalogueGameId: state.library.some((row) => row.game?.id === dota?.id) ? null : (dota?.id ?? null),
          inLibrary: state.library.some((row) => row.game?.id === dota?.id),
        },
        { source: 'steam', sourceId: '999999', name: 'Indie Thing', versions: [], stations: ['PC 2'], catalogueGameId: null, inLibrary: false },
      ],
    };
  }

  // -- The organisation's custom apps (spec/13 §9) -------------------------------------------------

  /** The unarchived custom apps, each marked whether this venue offers it. */
  function venueCustomApps(method: string, query: URLSearchParams): () => FakeResponse {
    if (method !== 'GET') return methodNotAllowed;
    return () => {
      const parsed = PageQuerySchema.safeParse(Object.fromEntries(query));
      if (!parsed.success) return invalid(parsed.error);
      const rows = state.customApps
        .filter((app) => app.archivedAt === null)
        .map((app) => {
          const held = state.library.find((row) => row.customApp?.id === app.id);
          return { customApp: app, inLibrary: held !== undefined, entryId: held?.id ?? null };
        });
      return json(200, paged(rows, parsed.data.cursor, parsed.data.limit));
    };
  }

  /** Keep every library entry's embedded custom app in step with an edit (as the api reads it fresh). */
  function refreshEntries(app: CustomApp): void {
    state.library = state.library.map((row) => (row.customApp?.id === app.id ? { ...row, customApp: app } : row));
  }

  function customApps(method: string, rest: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    const nameTaken = (name: string, except?: string) =>
      state.customApps.some((app) => app.archivedAt === null && app.id !== except && app.name.toLowerCase() === name.trim().toLowerCase());
    if (rest === '') {
      if (method === 'GET') {
        return () => {
          const parsed = PageQuerySchema.safeParse(Object.fromEntries(query));
          if (!parsed.success) return invalid(parsed.error);
          return json(200, paged(state.customApps.filter((app) => app.archivedAt === null), parsed.data.cursor, parsed.data.limit));
        };
      }
      if (method !== 'POST') return methodNotAllowed;
      return () => {
        const parsed = CreateCustomAppSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        if (nameTaken(parsed.data.name)) return conflict('Another custom app in this organisation already has that name.');
        const created: CustomApp = {
          id: next('app', 'c'),
          organizationId: ORGANIZATION_ID,
          name: parsed.data.name.trim(),
          suggestedCategory: parsed.data.suggestedCategory,
          iconUrl: null,
          launchRecipes: parsed.data.launchRecipes,
          detection: normaliseTitleDetection(parsed.data.detection ?? { processNames: [] }),
          archivedAt: null,
          createdAt: NOW,
          updatedAt: NOW,
        };
        state.customApps.push(created);
        return json(201, created);
      };
    }
    const match = /^\/([^/]+)(\/archive|\/icon|\/icon\/([^/]+)\/complete)?$/.exec(rest);
    if (match === null) return undefined;
    const appId = match[1];
    const tail = match[2] ?? '';
    const held = () => state.customApps.find((app) => app.id === appId && app.archivedAt === null);
    const save = (app: CustomApp) => {
      state.customApps = state.customApps.map((row) => (row.id === app.id ? app : row));
      refreshEntries(app);
      return app;
    };
    if (tail === '' && method === 'PATCH') {
      return () => {
        const parsed = UpdateCustomAppSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const found = held();
        if (found === undefined) return notFound('No such custom app.');
        if (parsed.data.name !== undefined && nameTaken(parsed.data.name, found.id)) return conflict('Another custom app in this organisation already has that name.');
        return json(
          200,
          save({
            ...found,
            ...(parsed.data.name === undefined ? {} : { name: parsed.data.name.trim() }),
            ...(parsed.data.suggestedCategory === undefined ? {} : { suggestedCategory: parsed.data.suggestedCategory }),
            ...(parsed.data.launchRecipes === undefined ? {} : { launchRecipes: parsed.data.launchRecipes }),
            ...(parsed.data.detection === undefined ? {} : { detection: normaliseTitleDetection(parsed.data.detection) }),
            updatedAt: NOW,
          }),
        );
      };
    }
    if (tail === '/archive' && method === 'POST') {
      return () => {
        const found = state.customApps.find((app) => app.id === appId);
        if (found === undefined) return notFound('No such custom app.');
        return json(200, found.archivedAt === null ? save({ ...found, archivedAt: NOW, updatedAt: NOW }) : found);
      };
    }
    if (tail === '/icon' && method === 'POST') {
      return () => {
        const parsed = CreateAppIconUploadSchema.safeParse(input);
        if (!parsed.success) return invalid(parsed.error);
        const found = held();
        if (found === undefined) return notFound('No such custom app.');
        const uploadId = '0199e100-0000-7000-8000-0000000000a1';
        return json(201, {
          uploadId,
          upload: { method: 'PUT', url: `https://storage.test/public-media/orgs/${ORGANIZATION_ID}/apps/${found.id}/uploads/${uploadId}`, contentType: parsed.data.contentType, expiresAt: NOW },
        });
      };
    }
    if (match[3] !== undefined && method === 'POST') {
      return () => {
        const found = held();
        if (found === undefined) return notFound('No such custom app.');
        return json(200, save({ ...found, iconUrl: `https://cdn.clavian.test/orgs/${ORGANIZATION_ID}/apps/${found.id}/${'c'.repeat(64)}.png`, updatedAt: NOW }));
      };
    }
    return methodNotAllowed;
  }

  // -- Routing --------------------------------------------------------------------------------

  const organizationPath = `/v1/organizations/${ORGANIZATION_ID}`;
  const venuePath = `${organizationPath}/venues/${VENUE_ID}`;

  /** The answer for a path this module owns, not yet run; `undefined` for any other path. */
  function route(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): (() => FakeResponse) | undefined {
    if (pathname === `${venuePath}/shifts` || pathname.startsWith(`${venuePath}/shifts/`)) {
      return shifts(method, pathname.slice(`${venuePath}/shifts`.length), query, input);
    }
    if (pathname === `${venuePath}/counters` || pathname.startsWith(`${venuePath}/counters/`)) {
      return countersRoute(method, pathname.slice(`${venuePath}/counters`.length), query, input);
    }
    if (pathname === `${venuePath}/day-close`) return dayClose(method, input, query);
    if (pathname === `${venuePath}/day-close/sign-off`) return signOff(method, input);
    if (pathname === `${venuePath}/business-day`) {
      if (method !== 'GET') return methodNotAllowed;
      return () => json(200, businessDayStatus());
    }
    if (pathname === `${venuePath}/library` || pathname.startsWith(`${venuePath}/library/`)) {
      return library(method, pathname.slice(`${venuePath}/library`.length), query, input);
    }
    if (pathname === `${venuePath}/catalogue`) return catalogue(method, query);
    if (pathname === `${venuePath}/custom-apps`) return venueCustomApps(method, query);
    if (pathname === `${organizationPath}/custom-apps` || pathname.startsWith(`${organizationPath}/custom-apps/`)) {
      return customApps(method, pathname.slice(`${organizationPath}/custom-apps`.length), query, input);
    }
    if (pathname.startsWith(`${organizationPath}/passes/`)) {
      return passes(method, pathname.slice(`${organizationPath}/passes`.length), query, input);
    }
    return undefined;
  }

  /** `undefined` for a request that is not the till's, the passes' or the library's. */
  function handle(method: string, pathname: string, query: URLSearchParams, input: Record<string, unknown>): FakeResponse | undefined {
    const answer = route(method.toUpperCase(), pathname, query, input);
    if (answer === undefined) return undefined;
    return helpers.gate() ?? answer();
  }

  /**
   * A counter pass line's name and price from the product's live version, or why the counter cannot sell it
   * (F-POS-01) — the commerce fake's `passes` hook, as `PassService.priceForCounter` is the server's. Sale windows
   * and purchase limits are the server's to check; the fixtures carry none that bite.
   */
  function priceForCounter(passProductId: string): { name: string; price: Money } | { refused: string } | undefined {
    const found = state.products.find((one) => one.id === passProductId);
    if (found === undefined) return undefined;
    const terms = found.version;
    if (!found.active) return { refused: `"${terms.name}" is not on sale.` };
    if (!terms.channels.includes('counter')) return { refused: `"${terms.name}" is not sold at the counter.` };
    if ((terms.membersOnly ?? []).length > 0) return { refused: `"${terms.name}" is for members only.` };
    return { name: terms.name, price: terms.price };
  }

  /**
   * Would cash the signed-in member takes now be refused (`cash-needs-shift`, ADR-0031)? When the venue counts drawers
   * and they are on no open drawer — unless the venue shares one drawer and it is open — as the console's own lock reads
   * it (`useCashLocked`). The wallet's top-up fake asks it.
   */
  function cashNeedsShift(): boolean {
    if (!shiftsEnabled()) return false;
    const open = openDrawers();
    if (open.some((row) => onDrawer(row, ME))) return false;
    return !(cashHandling() === 'shared_drawer' && open.length === 1);
  }

  return { state, handle, priceForCounter, cashNeedsShift };
}

export type FakeOps = ReturnType<typeof createFakeOps>;
